import type { Lang } from "@/lib/i18n/config";
import { LOCALIZED_ARTICLES, LOCALIZED_TOPICS } from "@/lib/help-articles-localized";

/**
 * Hjälpbanken. Alla texter i hjälpcentret ligger här så att sökning,
 * ämnesgrupper och förslagen i supportformuläret använder exakt samma innehåll.
 */

export type HelpTopicId =
  | "konto"
  | "annons"
  | "inkorg"
  | "auktion"
  | "niva"
  | "skanning"
  | "app"
  | "teknik";

export type HelpTopic = { id: HelpTopicId; label: string; blurb: string };

export const HELP_TOPICS: HelpTopic[] = [
  { id: "konto", label: "Konto och inloggning", blurb: "Skapa konto, koder, lösenord och profil." },
  { id: "annons", label: "Annonser och publicering", blurb: "Skapa, ändra, pausa och förnya." },
  {
    id: "inkorg",
    label: "Inkorg och kontakt",
    blurb: "Meddelanden, bud i tråden, olästa och anmälningar.",
  },
  { id: "auktion", label: "Auktion och bud", blurb: "Lägga bud, sluttider och vinnare." },
  { id: "niva", label: "Nivåer och betalning", blurb: "Vad som ingår, byta nivå och kvitton." },
  { id: "skanning", label: "Skanning och värdering", blurb: "Fotoskanning, film och prisförslag." },
  { id: "app", label: "Appen på mobilen", blurb: "Lägg till på hemskärmen och slå på notiser." },
  { id: "teknik", label: "Teknik och fel", blurb: "När något krånglar eller ser konstigt ut." },
];


export function topicLabel(id: string): string {
  return HELP_TOPICS.find((t) => t.id === id)?.label ?? "Övrigt";
}

export type HelpLinkRef = { to: string; label: string };

export type HelpArticle = {
  id: string;
  topic: HelpTopicId;
  title: string;
  /** Kort svar som visas direkt, en mening. */
  summary: string;
  /** Steg för steg eller förklarande stycken. */
  steps: string[];
  links?: HelpLinkRef[];
  keywords: string[];
  /** Visas bland genvägarna högst upp. */
  popular?: boolean;
  /** Sätts på artikeln som ska rendera installationsguiden. */
  special?: "install";
};

export const HELP_ARTICLES: HelpArticle[] = [
  {
    id: "kod-kommer-inte",
    topic: "konto",
    title: "Jag får ingen kod till min e-post",
    summary: "Koden kommer inom en minut. Kolla skräpposten och att adressen är rätt stavad.",
    steps: [
      "Vänta en minut – koden skickas direkt men kan fastna en kort stund hos din e-postleverantör.",
      "Titta i skräpposten och i fliken Kampanjer om du har Gmail.",
      "Kontrollera att adressen är rätt stavad. En bokstav fel och mejlet hamnar hos någon annan.",
      "Begär en ny kod. Den gamla slutar gälla direkt när en ny skickas.",
      "Får du fortfarande inget mejl: skicka ett ärende till oss så hjälper vi dig in i kontot.",
    ],
    keywords: ["kod", "mejl", "e-post", "registrera", "logga in", "inloggning", "bekräfta", "otp"],
    popular: true,
  },
  {
    id: "logga-in-satt",
    topic: "konto",
    title: "Logga in med Google eller e-post",
    summary: "Du kan logga in med Google eller med e-post och lösenord – samma konto.",
    steps: [
      "Har du skapat kontot med Google loggar du alltid in med Google-knappen.",
      "Har du e-post och lösenord kan du byta lösenord via Glömt lösenord.",
      "Under Mitt konto ser du vilket inloggningssätt ditt konto använder.",
    ],
    links: [
      { to: "/konto", label: "Mitt konto" },
      { to: "/glomt-losenord", label: "Glömt lösenord" },
    ],
    keywords: ["google", "lösenord", "logga in", "inloggning", "konto"],
  },
  {
    id: "profil-namn",
    topic: "konto",
    title: "Byta visningsnamn, bild och kontaktuppgifter",
    summary: "Allt som syns utåt ändrar du under Min profil.",
    steps: [
      "Gå till Min profil och ändra visningsnamn, bild och ort.",
      "Ditt riktiga namn visas aldrig i annonser – bara visningsnamnet.",
      "E-post och telefon används för kontakt och visas inte offentligt.",
    ],
    links: [
      { to: "/profil", label: "Min profil" },
      { to: "/adresser", label: "Mina adresser" },
    ],
    keywords: ["profil", "namn", "visningsnamn", "bild", "avatar", "adress", "ort"],
  },
  {
    id: "ta-bort-konto",
    topic: "konto",
    title: "Ta bort mitt konto",
    summary: "Under Mitt konto finns en riskzon där du tar bort kontot med en kod.",
    steps: [
      "Gå till Mitt konto och skrolla längst ner till riskzonen.",
      "Begär koden, klistra in den från mejlet och bekräfta.",
      "Du har 30 dagar på dig att ångra dig – logga bara in igen inom den tiden.",
      "Efter 30 dagar raderas kontot, dina annonser och dina meddelanden.",
    ],
    links: [{ to: "/konto", label: "Mitt konto" }],
    keywords: ["ta bort", "radera", "avsluta", "säga upp", "konto", "ångra"],
  },
  {
    id: "skapa-annons",
    topic: "annons",
    title: "Så skapar du en annons",
    summary: "Fota, låt oss skriva texten och justera pris innan du publicerar.",
    steps: [
      "Tryck Skapa och lägg in bilder på prylen. Bra ljus och en ren bakgrund ger bättre resultat.",
      "Vi föreslår rubrik, beskrivning och pris utifrån bilderna. Allt går att ändra.",
      "Välj ort, säljsätt (fast pris eller auktion) och kontrollera kontaktuppgifterna.",
      "Tryck Publicera. Du får ett bekräftelsemejl när annonsen ligger ute.",
    ],
    links: [
      { to: "/skapa", label: "Skapa annons" },
      { to: "/mina-annonser", label: "Mina annonser" },
    ],
    keywords: ["skapa", "publicera", "annons", "fota", "bilder", "pris", "text"],
    popular: true,
  },
  {
    id: "andra-annons",
    topic: "annons",
    title: "Ändra, pausa eller ta bort en annons",
    summary: "Gå till Mina annonser och välj Redigera på annonsen.",
    steps: [
      "Öppna Mina annonser och tryck på annonsen du vill ändra.",
      "I Redigera kan du ändra text, pris, bilder, ort och säljsätt.",
      "Pausa döljer annonsen utan att ta bort den – du kan aktivera den igen när du vill.",
      "Ta bort raderar annonsen helt. Meddelanden om den finns kvar i din inkorg.",
    ],
    links: [{ to: "/mina-annonser", label: "Mina annonser" }],
    keywords: ["ändra", "redigera", "pausa", "ta bort", "annons", "pris", "bild"],
    popular: true,
  },
  {
    id: "annons-stoppad",
    topic: "annons",
    title: "Varför stoppades min annons innan publicering?",
    summary: "Vi granskar text, bilder och pris automatiskt innan annonsen går ut.",
    steps: [
      "Vanligast är att flera helt olika saker blandas i samma annons – dela upp dem.",
      "En person som syns tydligt på en bild stoppar också publiceringen.",
      "Priset kan flaggas om det ligger långt ifrån vad liknande saker säljs för.",
      "Justera det som markeras och prova igen – du behöver inte börja om.",
    ],
    links: [{ to: "/regler", label: "Regler och villkor" }],
    keywords: ["stoppad", "nekad", "granskning", "publicera", "regler", "varning"],
  },
  {
    id: "lyft-annons",
    topic: "annons",
    title: "Lyfta annonsen högst upp",
    summary: "Med Plus och uppåt kan du lyfta en annons så att den syns först.",
    steps: [
      "Öppna annonsen under Mina annonser och välj Lyft.",
      "Ett lyft bokas i hela steg och varje dygn kostar en lyftdag ur månadskvoten.",
      "Under Din nivå ser du hur många lyftdagar du har kvar.",
    ],
    links: [
      { to: "/lyft", label: "Lyft" },
      { to: "/min-niva", label: "Din nivå" },
    ],
    keywords: ["lyft", "toppa", "synlighet", "boost", "lyftdagar"],
  },
  {
    id: "auktion-sa-funkar",
    topic: "auktion",
    title: "Så fungerar auktioner och bud",
    summary: "Sätt utgångspris och sluttid – högsta budet vinner när tiden går ut.",
    steps: [
      "Som säljare väljer du auktion när du skapar annonsen, sätter utgångspris och hur länge den ska pågå.",
      "Som köpare lägger du bud direkt på annonssidan. Vi föreslår nästa giltiga bud.",
      "Ett bud under de sista fem minuterna förlänger auktionen med fem minuter, så ingen kan vinna på målsnöret.",
      "När auktionen stängs får vinnaren och säljaren kontakt automatiskt i inkorgen.",
    ],
    links: [
      { to: "/mina-bud", label: "Mina bud" },
      { to: "/annonser", label: "Alla annonser" },
    ],
    keywords: ["auktion", "bud", "budgivning", "vinna", "sluttid", "förlängning"],
    popular: true,
  },
  {
    id: "mina-bud",
    topic: "auktion",
    title: "Var ser jag mina bud och vad jag vunnit?",
    summary: "Under Mina bud finns pågående, vunna och avslutade auktioner.",
    steps: [
      "Öppna Mina bud i menyn.",
      "Pågående visar där du är med just nu och om du har högsta budet.",
      "Vunna visar vad du har vunnit och hur du får kontakt med säljaren.",
    ],
    links: [{ to: "/mina-bud", label: "Mina bud" }],
    keywords: ["bud", "vunnit", "köp", "auktion", "historik"],
  },
  {
    id: "vad-ingar",
    topic: "niva",
    title: "Vad ingår i min nivå?",
    summary: "På sidan Din nivå ser du exakt vad ditt konto innehåller och vilka gränser som gäller.",
    steps: [
      "Öppna Din nivå för att se annonser kvar, bilder per annons, lyft och video.",
      "Vill du jämföra nivåerna mot varandra finns allt på prissidan.",
      "Betalning, kvitton och fakturaperiod hittar du under Mitt konto.",
    ],
    links: [
      { to: "/min-niva", label: "Din nivå" },
      { to: "/priser", label: "Jämför nivåer" },
      { to: "/konto", label: "Mitt konto" },
    ],
    keywords: ["nivå", "plan", "plus", "pro", "expert", "gratis", "ingår", "gränser"],
    popular: true,
  },
  {
    id: "byta-niva",
    topic: "niva",
    title: "Byta nivå eller säga upp",
    summary: "Du kan byta nivå när som helst. Ingen bindningstid.",
    steps: [
      "Betalning öppnar snart. Fram till dess hör du av dig till oss så flyttar vi upp ditt konto.",
      "Går du ner en nivå gäller den du betalat för perioden ut.",
      "Årsbetalning motsvarar tio månader i stället för tolv.",
    ],
    links: [
      { to: "/priser", label: "Priser" },
      { to: "/konto", label: "Mitt konto" },
    ],
    keywords: ["byta", "uppgradera", "nedgradera", "säga upp", "betala", "faktura", "kvitto"],
  },
  {
    id: "varfor-kostar",
    topic: "niva",
    title: "Varför kostar vissa saker?",
    summary: "Bildanalys, skanning och värdering drar riktig datorkraft – därför nivåerna.",
    steps: [
      "Att lägga ut annonser är gratis inom gratisnivåns gränser.",
      "Fler annonser, fler bilder, video och värderingar ingår i de betalda nivåerna.",
      "Vi förklarar hela resonemanget på en egen sida.",
    ],
    links: [{ to: "/varfor-kostar-det", label: "Varför kostar det?" }],
    keywords: ["pris", "kostar", "gratis", "varför", "betala"],
  },
  {
    id: "fotoskanning",
    topic: "skanning",
    title: "Fotoskanning och videoskanning",
    summary: "Ladda upp flera bilder eller en film så plockar vi ut prylarna åt dig.",
    steps: [
      "Fotoskanning: ladda upp många bilder på en gång så grupperar vi dem per pryl.",
      "Videoskanning: filma ett rum lugnt och långsamt så listar vi vad vi hittar med ungefärliga värden.",
      "Allt vi hittar sparas under Mina prylar tills du gör annonser av dem.",
      "Skanning ingår från Plus (foto) och Pro (video).",
    ],
    links: [
      { to: "/videoskanning", label: "Videoskanning" },
      { to: "/mina-prylar", label: "Mina prylar" },
    ],
    keywords: ["skanna", "skanning", "film", "video", "foto", "prylar", "lista"],
  },
  {
    id: "vardering",
    topic: "skanning",
    title: "Hur säkra är prisförslagen?",
    summary: "Priser vi föreslår är uppskattningar – du bestämmer alltid själv.",
    steps: [
      "Förslagen bygger på hur liknande saker säljs och på vad vi ser i bilderna.",
      "Djupvärdering ställer följdfrågor och tar fram jämförbara försäljningar för dyrare saker.",
      "Det är alltid en uppskattning och ingen formell värdering.",
    ],
    links: [{ to: "/min-niva", label: "Din nivå" }],
    keywords: ["värdering", "pris", "prisförslag", "djupvärdering", "uppskattning"],
  },
  {
    id: "lagg-till-pa-mobilen",
    topic: "app",
    title: "Lägg till Sellavo på din mobil",
    summary: "Lägg Sellavo på hemskärmen så fungerar den som en app – och notiser kan slås på.",
    steps: [
      "iPhone och iPad: öppna Sellavo i Safari, tryck på dela-ikonen längst ner, välj Lägg till på hemskärmen och tryck Lägg till.",
      "Android: öppna Sellavo i Chrome, tryck på menyn med tre punkter uppe till höger och välj Installera app eller Lägg till på hemskärmen.",
      "Öppna sedan Sellavo från ikonen på hemskärmen i stället för i webbläsaren.",
      "På iPhone måste appen ligga på hemskärmen för att notiser ska gå att slå på alls.",
    ],
    links: [{ to: "/konto", label: "Notisinställningar" }],
    keywords: ["app", "installera", "hemskärm", "iphone", "ios", "safari", "android", "chrome", "pwa"],
    popular: true,
    special: "install",
  },
  {
    id: "notiser",
    topic: "app",
    title: "Slå på notiser",
    summary: "Notiser slås på under Mitt konto – på iPhone krävs att appen ligger på hemskärmen.",
    steps: [
      "Lägg först till Sellavo på hemskärmen om du har iPhone.",
      "Gå till Mitt konto och slå på notiser. Godkänn frågan från telefonen.",
      "Du kan välja bort mejl separat om du bara vill ha notiser.",
      "Får du inga notiser: kontrollera att de är tillåtna för Sellavo i telefonens inställningar.",
    ],
    links: [{ to: "/konto", label: "Mitt konto" }],
    keywords: ["notis", "notiser", "push", "meddelande", "iphone", "android", "ljud"],
  },
  {
    id: "meddelanden",
    topic: "inkorg",
    title: "Var hamnar meddelanden från köpare?",
    summary: "All kontakt sker i inkorgen på Sellavo – inga adresser eller telefonnummer visas.",
    steps: [
      "Öppna Inkorg för att se alla trådar, både mottagna och skickade.",
      "Du får notis och mejl när någon svarar dig.",
      "Vi visar aldrig din e-post eller ditt telefonnummer för andra användare.",
    ],
    links: [{ to: "/meddelanden", label: "Inkorg" }],
    keywords: ["meddelande", "inkorg", "köpare", "svar", "kontakt"],
    popular: true,
  },
  {
    id: "olasta-forsvinner-inte",
    topic: "inkorg",
    title: "Siffran för olästa försvinner inte",
    summary: "Olästa räknas ner när du öppnar själva konversationen, inte bara inkorgslistan.",
    steps: [
      "Öppna inkorgen och klicka på konversationen i listan till vänster.",
      "När tråden visas markeras alla meddelanden i den som lästa och siffran i sidhuvudet räknas ner.",
      "Har du många trådar: sortera på Olästa först så ligger de överst.",
      "Ligger siffran kvar efter att du läst allt – ladda om sidan, den uppdateras var femtonde sekund.",
    ],
    links: [{ to: "/meddelanden", label: "Inkorg" }],
    keywords: ["oläst", "olästa", "siffra", "badge", "räknare", "inkorg"],
  },
  {
    id: "sortera-inkorg",
    topic: "inkorg",
    title: "Filtrera, sortera och rensa i inkorgen",
    summary: "Du kan filtrera på mottagna eller skickade, sortera listan och arkivera gamla trådar.",
    steps: [
      "Flikarna Alla, Mottagna och Skickade delar upp trådarna efter vem som hörde av sig.",
      "Sortera på Senast kontaktad, Olästa först, Mina annonser först eller Mina utskick först.",
      "Arkivera en tråd när affären är klar – den försvinner ur listan men finns kvar.",
      "Tar du bort en tråd döljs den bara för dig. Motparten behåller sin kopia.",
    ],
    links: [{ to: "/meddelanden", label: "Inkorg" }],
    keywords: ["sortera", "filter", "arkivera", "ta bort", "rensa", "inkorg", "trådar"],
  },
  {
    id: "bud-i-inkorgen",
    topic: "inkorg",
    title: "Bud och förfrågningar i inkorgen",
    summary: "Bud, prisförfrågningar och svar på dem ligger i samma tråd som annonsen.",
    steps: [
      "Får du ett bud syns det som ett eget kort i tråden med belopp och knappar för att acceptera eller avböja.",
      "Accepterar du budet får köparen ett meddelande direkt och ni fortsätter i samma tråd.",
      "Prisförfrågningar fungerar likadant – du kan svara med ett motbud.",
      "Vill du inte ta emot bud alls kan du stänga av det när du skapar eller redigerar annonsen.",
    ],
    links: [{ to: "/meddelanden", label: "Inkorg" }],
    keywords: ["bud", "budgivning", "prisförfrågan", "acceptera", "motbud", "inkorg"],
  },
  {
    id: "anmal-meddelande",
    topic: "inkorg",
    title: "Anmäl ett meddelande eller en användare",
    summary: "Bedrägeriförsök, hot eller spam anmäler du direkt i tråden så tittar vi på det.",
    steps: [
      "Öppna tråden och välj Anmäl på meddelandet det gäller.",
      "Skriv kort vad som hänt. Vi ser hela konversationen när vi granskar.",
      "Svara aldrig med kontokort, BankID-koder eller länkar du fått av en okänd köpare.",
      "Vill du hellre prata med oss direkt: skicka ett supportärende så återkommer vi.",
    ],
    links: [{ to: "/support", label: "Kontakta supporten" }],
    keywords: ["anmäl", "bedrägeri", "spam", "hot", "blockera", "rapportera"],
  },

  {
    id: "bilder-krangar",
    topic: "teknik",
    title: "Bilder laddas inte upp eller ser konstiga ut",
    summary: "Prova en mindre bild, byt nätverk och ladda om sidan.",
    steps: [
      "Kontrollera att du har täckning – stora bilder tar tid på svagt nät.",
      "Ladda om sidan och prova igen. Påbörjad annons sparas åt dig.",
      "Ser bilden vit eller vänd ut: ta om bilden i telefonens kamera-app och ladda upp den igen.",
      "Krånglar det fortfarande, skicka ett ärende med en skärmdump så tittar vi på det.",
    ],
    keywords: ["bild", "uppladdning", "fel", "vit", "roterad", "laddar"],
  },
  {
    id: "sidan-krangar",
    topic: "teknik",
    title: "Sidan hänger sig eller ser trasig ut",
    summary: "Ladda om, och stäng appen helt om du kör den från hemskärmen.",
    steps: [
      "Dra ner för att ladda om sidan.",
      "Kör du appen från hemskärmen: stäng den helt och öppna igen så hämtas senaste versionen.",
      "Hjälper inte det, skicka ett ärende. Vi ser automatiskt vilken sida och enhet du använde.",
    ],
    keywords: ["fel", "trasig", "hänger", "buggar", "vit skärm", "laddar inte"],
  },
];

export function helpTopics(lang: Lang): HelpTopic[] {
  return lang === "sv" ? HELP_TOPICS : LOCALIZED_TOPICS[lang];
}

export function helpArticles(lang: Lang): HelpArticle[] {
  if (lang === "sv") return HELP_ARTICLES;
  return HELP_ARTICLES.map((article) => ({
    ...article,
    ...LOCALIZED_ARTICLES[lang][article.id],
    links: LOCALIZED_ARTICLES[lang][article.id]?.links ?? article.links,
  }));
}

/** Alla artiklar i ett ämne. */
export function articlesByTopic(topic: HelpTopicId, lang: Lang = "sv"): HelpArticle[] {
  return helpArticles(lang).filter((a) => a.topic === topic);
}

export function findArticle(id: string | undefined, lang: Lang = "sv"): HelpArticle | null {
  if (!id) return null;
  return helpArticles(lang).find((a) => a.id === id) ?? null;
}

export const POPULAR_ARTICLES = HELP_ARTICLES.filter((a) => a.popular);

export function popularArticles(lang: Lang): HelpArticle[] {
  return helpArticles(lang).filter((a) => a.popular);
}

function words(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-zà-ÿ0-9]+/i)
    .filter((w) => w.length > 2);
}

/** Fritextsökning i hjälpbanken. Enkel ordträff med vikt på rubrik och nyckelord. */
export function searchHelp(query: string, limit = 20, lang: Lang = "sv"): HelpArticle[] {
  const w = words(query);
  if (!w.length) return [];
  return helpArticles(lang).map((a) => {
    const title = a.title.toLowerCase();
    const keys = a.keywords.join(" ").toLowerCase();
    const body = `${a.summary} ${a.steps.join(" ")}`.toLowerCase();
    const score = w.reduce(
      (sum, word) =>
        sum +
        (title.includes(word) ? 3 : 0) +
        (keys.includes(word) ? 2 : 0) +
        (body.includes(word) ? 1 : 0),
      0,
    );
    return { a, score };
  })
    .filter((r) => r.score > 0)
    .sort((x, y) => y.score - x.score)
    .slice(0, limit)
    .map((r) => r.a);
}

/** Förslagen som visas i supportformuläret medan man skriver. */
export function helpMatches(text: string, limit = 3, lang: Lang = "sv"): HelpArticle[] {
  return searchHelp(text, limit, lang);
}
